// @ts-ignore
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyCallback = (...args: any[]) => any;
type EventKey = string | number;

declare global {
	interface Window {
		__COMMUNICATION_BUS__: Record<string, Record<string, Array<AnyCallback>>>;
	}
}

window.__COMMUNICATION_BUS__ = window.__COMMUNICATION_BUS__ || {};

export const syncManager = {
	/**
	 * Registers a callback function to be called when the specified event is emitted.
	 * @param {EventKey} moduleName - The name of the module to listen to.
	 * @param {EventKey} eventName - The name of the event to listen to.
	 * @param {AnyCallback} fn - The callback function to register.
	 * @returns {Function} - A function that can be called to unsubscribe the registered callback function.
	 */
	on(moduleName: EventKey, eventName: EventKey, fn: AnyCallback) {
		window.__COMMUNICATION_BUS__[moduleName] = window.__COMMUNICATION_BUS__[moduleName] || {};
		window.__COMMUNICATION_BUS__[moduleName][eventName] = window.__COMMUNICATION_BUS__[moduleName][eventName] || [];
		window.__COMMUNICATION_BUS__[moduleName][eventName].push(fn);

		return () => {
			syncManager.unsubscribe(moduleName, eventName, fn);
		};
	},

	/**
	 * Removes all callback functions registered for the specified event on the specified module.
	 * @param {EventKey} moduleName - The name of the module to unsubscribe from.
	 * @param {EventKey} eventName - The name of the event to unsubscribe from.
	 */
	off(moduleName: EventKey, eventName: EventKey) {
		if (window.__COMMUNICATION_BUS__[moduleName][eventName]) {
			for (let i = 0; i < window.__COMMUNICATION_BUS__[moduleName][eventName].length; i += 1) {
				window.__COMMUNICATION_BUS__[moduleName][eventName].splice(i, 1);
			}
		}
	},

	/**
	 * Emits the specified event to all registered callback functions on the specified module.
	 * @param {EventKey} moduleName - The name of the module to emit the event to.
	 * @param {EventKey} eventName - The name of the event to emit.
	 * @param {*} [data] - Optional data to pass to the callback functions.
	 */
	emit(moduleName: EventKey, eventName: EventKey, data?: unknown) {
		if (window.__COMMUNICATION_BUS__[moduleName] && window.__COMMUNICATION_BUS__[moduleName][eventName]) {
			window.__COMMUNICATION_BUS__[moduleName][eventName].forEach((fn) => {
				fn(data);
			});
		}
	},

	/**
	 * Unsubscribes a specific callback function from the specified event on the specified module.
	 * @param {EventKey} moduleName - The name of the module to unsubscribe from.
	 * @param {EventKey} eventName - The name of the event to unsubscribe from.
	 * @param {AnyCallback} fnToUnsubscribe - The specific callback function to unsubscribe.
	 */
	unsubscribe(moduleName: EventKey, eventName: EventKey, fnToUnsubscribe: AnyCallback) {
		if (window.__COMMUNICATION_BUS__[moduleName][eventName]) {
			window.__COMMUNICATION_BUS__[moduleName][eventName] = window.__COMMUNICATION_BUS__[moduleName][
				eventName
			].filter((fn) => fn !== fnToUnsubscribe);
		}
	},
};
